import { AppServices } from 'ha/services/getAppServices';
import { normalizeListing, AlgoliaListing } from '@ha/algolia';
import { AlgoliaQuery } from 'ha/services/algolia';
import { NormalizedAlgoliaListing } from 'ha/models/Listing/types';
import { normalizeAlgoliaListing } from 'ha/models/Listing/normalizeAlgoliaListing';

import { Listing } from '@ha/api/v2/types/Listing';

import { normalizeApiV2Listing } from 'ha/models/Listing/normalizeApiV2Listing';
import { ListingEntity } from '../types';

const SEARCH_RADIUS = 15000;

const SEARCH_PRICE_RANGE_DIFF = 0.2;

const DEFAULT_CURRENCY = 'EUR';

export async function fetchSimilarListings(
  listing: ListingEntity,
  { apiV2, algolia }: AppServices,
): Promise<NormalizedAlgoliaListing[]> {
  const {
    data: { rates },
  } = await apiV2.getCurrencyEuroRates();

  const currency = listing.currency || DEFAULT_CURRENCY;

  const currencyRate = rates[currency];

  const localNormalizeListing = normalizeListing({
    currency,
    currencyRate,
  });

  const radius = SEARCH_RADIUS;

  const price = listing.price / currencyRate;

  const priceMin = price * (1 - SEARCH_PRICE_RANGE_DIFF);
  const priceMax = price * (1 + SEARCH_PRICE_RANGE_DIFF);

  const query: AlgoliaQuery = {
    query: '',
    params: {
      page: 0,
      hitsPerPage: 5,
      distinct: true,
      aroundLatLng: `${listing.latitude}, ${listing.longitude}`,
      aroundRadius: radius,
      filters: `priceEUR>=${priceMin / 100} AND priceEUR<=${
        priceMax / 100
      } AND isSearchable:true AND advertiserId!=1579986`, // remove Studapart listings
    },
  };

  const { results } = await algolia.searchListings([query]);

  let result = results[0].hits.map((algoliaListing: AlgoliaListing) =>
    normalizeAlgoliaListing(localNormalizeListing(algoliaListing)),
  );

  // do not include own listing
  result = result.filter(
    item => item.id !== listing.id && item.unitTypeId !== listing.id,
  );

  // limit results to 4
  if (result.length > 4) {
    result.length = 4;
  }

  return result;
}

interface MULListing extends ListingEntity {
  unitType?: {
    id: string;
  };
}

export async function fetchSimilarListingsFromV2(
  listing: MULListing,
  { apiV2 }: AppServices,
): Promise<NormalizedAlgoliaListing[]> {
  const listings = await apiV2.getSimilarListings({
    listingId: `${listing?.unitTypeId ?? listing.id}`,
    expand: 'photos,exclusions,costs,units',
  });

  const normalizedListings = listings?.data?.map((apiListing: Listing) =>
    normalizeApiV2Listing(apiListing),
  );

  const result = normalizedListings?.filter(
    (item: NormalizedAlgoliaListing) =>
      item.id !== listing.id && item.unitTypeId !== listing.id,
  );

  return result;
}
